'use client'

import type { SbBlokData } from '@storyblok/react'
import { StoryblokComponent } from '@storyblok/react'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import clsx from 'clsx'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import type { SbBaseBlockProps, ExpectedBlockType } from '@/services/storyblok/storyblok'
import type { ImageBlockProps } from './ImageBlock/ImageBlock'
import {
  fluidContainer,
  fluidBodyWrapper,
  fluidBodyInnerWrapper,
  withMinHeightOnMobile,
  mediaPlacementMobileVar,
  mediaPlacementDesktopVar,
  textHorizontalAlignmentVar,
  textVerticalAlignmentVar,
  minHeightOnMobileVar,
} from './ImageTextBlock.css'
import type { VideoBlockProps } from './VideoBlock'

type Orientation = 'vertical' | 'fluid'
type TextHorizontalAlignment = 'left' | 'center' | 'right'
type TextVerticalAlignment = 'top' | 'center' | 'bottom'
type MediaPlacementMobile = 'top' | 'bottom'
type MediaPlacementDesktop = 'left' | 'right'

const DEFAULT_ORIENTATION: Orientation = 'vertical'
const DEFAULT_TEXT_HORIZONTAL_ALIGNMENT: TextHorizontalAlignment = 'left'
const DEFAULT_TEXT_VERTICAL_ALIGNMENT: TextVerticalAlignment = 'top'
const DEFAULT_MEDIA_PLACEMENT_MOBILE: MediaPlacementMobile = 'top'
const DEFAULT_MEDIA_PLACEMENT_DESKTOP: MediaPlacementDesktop = 'right'

export type ImageTextBlockProps = SbBaseBlockProps<{
  // TODO: Remove image field after content migration
  image: ExpectedBlockType<ImageBlockProps | VideoBlockProps>
  media: ExpectedBlockType<ImageBlockProps | VideoBlockProps>
  body?: Array<SbBlokData>
  orientation?: Orientation
  textHorizontalAlignment?: TextHorizontalAlignment
  // TODO: rename this to 'textVerticalAlignment'
  textAlignment?: TextVerticalAlignment
  mediaPlacementMobile?: MediaPlacementMobile
  // TODO: rename this to 'mediaPlacementDesktop' as it's in Storyblok
  imagePlacement?: MediaPlacementDesktop
  minHeightMobile?: string
}>

export const ImageTextBlock = ({ blok, nested }: ImageTextBlockProps) => {
  const { orientation = DEFAULT_ORIENTATION } = blok
  // We expect only one media object from Storyblok
  const imageBlock = Array.isArray(blok.image) ? blok.image[0] : undefined
  const mediaBlock = Array.isArray(blok.media) ? blok.media[0] : undefined

  switch (orientation) {
    case 'fluid':
      return (
        <FluidImageTextBlock
          mediaBlock={mediaBlock}
          imageBlock={imageBlock}
          body={blok.body}
          textAlignment={blok.textAlignment}
          textHorizontalAlignment={blok.textHorizontalAlignment}
          imagePlacement={blok.imagePlacement}
          mediaPlacementMobile={blok.mediaPlacementMobile}
          minHeightMobile={blok.minHeightMobile}
          nested={nested}
        />
      )
    case 'vertical':
      return <VerticalImageTextBlock mediaBlock={mediaBlock} imageBlock={imageBlock} body={blok.body} nested={nested} />
    default:
      console.warn(`orientation type '${orientation}' is not valid`)
      return null
  }
}
ImageTextBlock.blockName = 'imageText'

type VerticalImageTextBlockProps = Pick<ImageTextBlockProps['blok'], 'body'> & {
  imageBlock?: ImageBlockProps['blok'] | VideoBlockProps['blok']
  mediaBlock?: ImageBlockProps['blok'] | VideoBlockProps['blok']
  nested?: boolean
}

const VerticalImageTextBlock = ({ mediaBlock, imageBlock, body, nested }: VerticalImageTextBlockProps) => {
  return (
    <GridLayout.Root className={sprinkles({ rowGap: 'md' })} nested={nested}>
      {mediaBlock && (
        <GridLayout.Content width="1">
          <StoryblokComponent key={mediaBlock._uid} blok={mediaBlock} nested={true} />
        </GridLayout.Content>
      )}
      {imageBlock && (
        <GridLayout.Content width="1">
          <StoryblokComponent key={imageBlock._uid} blok={imageBlock} nested={true} />
        </GridLayout.Content>
      )}
      <GridLayout.Content className={sprinkles({ paddingInline: 'xs' })} width="1">
        {body?.map((nestedBlock) => (
          <StoryblokComponent key={nestedBlock._uid} blok={nestedBlock} nested={true} />
        ))}
      </GridLayout.Content>
    </GridLayout.Root>
  )
}

type FluidImageTextBlockProps = Pick<
  ImageTextBlockProps['blok'],
  'body' | 'textAlignment' | 'textHorizontalAlignment' | 'imagePlacement' | 'mediaPlacementMobile' | 'minHeightMobile'
> & {
  imageBlock?: ImageBlockProps['blok'] | VideoBlockProps['blok']
  mediaBlock?: ImageBlockProps['blok'] | VideoBlockProps['blok']
  nested?: boolean
}

export function FluidImageTextBlock({
  mediaBlock,
  imageBlock,
  body,
  textHorizontalAlignment = DEFAULT_TEXT_HORIZONTAL_ALIGNMENT,
  textAlignment = DEFAULT_TEXT_VERTICAL_ALIGNMENT,
  imagePlacement = DEFAULT_MEDIA_PLACEMENT_DESKTOP,
  mediaPlacementMobile = DEFAULT_MEDIA_PLACEMENT_MOBILE,
  minHeightMobile,
  nested,
}: FluidImageTextBlockProps) {
  return (
    <GridLayout.Root className={fluidContainer} nested={nested}>
      {mediaBlock && (
        <GridLayout.Content width="1/2">
          <StoryblokComponent key={mediaBlock._uid} blok={mediaBlock} nested={true} />
        </GridLayout.Content>
      )}
      {imageBlock && (
        <GridLayout.Content width="1/2">
          <StoryblokComponent key={imageBlock._uid} blok={imageBlock} nested={true} />
        </GridLayout.Content>
      )}
      <GridLayout.Content
        style={assignInlineVars({
          [mediaPlacementMobileVar]: mediaPlacementMobile === 'bottom' ? '-1' : 'initial',
          [mediaPlacementDesktopVar]: imagePlacement === 'right' ? '-1' : 'initial',
          [textHorizontalAlignmentVar]: getTextAlignmentStyles(textHorizontalAlignment),
          [textVerticalAlignmentVar]: getTextAlignmentStyles(textAlignment),
          [minHeightOnMobileVar]: minHeightMobile ? `${minHeightMobile}vh` : null,
        })}
        className={clsx(fluidBodyWrapper, minHeightMobile && withMinHeightOnMobile)}
        width={{ _: '1', lg: '1/2' }}
      >
        <div className={fluidBodyInnerWrapper}>
          {body?.map((nestedBlock) => (
            <StoryblokComponent key={nestedBlock._uid} blok={nestedBlock} nested={true} />
          ))}
        </div>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}

const getTextAlignmentStyles = (alignment: TextVerticalAlignment | TextHorizontalAlignment) => {
  switch (alignment) {
    case 'top':
    case 'left':
      return 'flex-start'
    case 'center':
      return 'center'
    case 'bottom':
    case 'right':
      return 'flex-end'
    default:
      return 'initial'
  }
}
